import { useState, useMemo, useCallback, useEffect } from 'react';
import { NPSDSearchFilters } from '@/components/npsd/NPSDSearchFilters';
import { NPSDSearchResults } from '@/components/npsd/NPSDSearchResults';
import { useNPSDSearch, NPSDSearchFilter } from '@/hooks/useNPSDSearch';

interface NPSDSearchBusinessProps {
  searchMode?: string;
  isMobile?: boolean;
  theme?: string;
  initialValues?: Record<string, any>;
  showNewButton?: boolean;
  onView?: (item: any) => void;
  onAdd?: (item: any) => void;
  onNew?: () => void;
}

export function NPSDSearchBusiness({
  searchMode = 'default',
  isMobile = false,
  theme = '',
  initialValues = {},
  showNewButton = false,
  onView,
  onAdd,
  onNew,
}: NPSDSearchBusinessProps) {
  const [businessName, setBusinessName] = useState('');
  const [branchName, setBranchName] = useState('');
  const [website, setWebsite] = useState('');
  const [address, setAddress] = useState('');

  const {
    results, totalRecords, currentPage, pageSize, isLoading, hasSearched,
    showViewButton, showAddButton,
    performSearch, clearResults, handlePageChange, handlePageSizeChange,
  } = useNPSDSearch({ entityApiName: 'Business', searchMode });

  const columns = useMemo(() => [
    { key: 'businessName', label: 'Business Name', field: 'businessName' },
    { key: 'branchName', label: 'Branch Name', field: 'branchName' },
    { key: 'website', label: 'Website', field: 'website' },
    { key: 'locFormattedAddress', label: 'Address', field: 'locFormattedAddress', fullWidth: true },
  ], []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (businessName) count++;
    if (branchName) count++;
    if (website) count++;
    if (address) count++;
    return count;
  }, [businessName, branchName, website, address]);

  // Apply initial values
  useEffect(() => {
    if (initialValues) {
      if (initialValues.businessName) setBusinessName(initialValues.businessName);
      if (initialValues.branchName) setBranchName(initialValues.branchName);
      if (initialValues.website) setWebsite(initialValues.website);
      if (initialValues.address) setAddress(initialValues.address);
    }
  }, [initialValues]);

  const buildFilters = useCallback((): NPSDSearchFilter[] => {
    const filters: NPSDSearchFilter[] = [];

    if (businessName) {
      filters.push({ field: 'businessName', operator: 'contains', value: businessName.trim() });
    }
    if (branchName) {
      filters.push({ field: 'branchName', operator: 'contains', value: branchName.trim() });
    }
    if (website) {
      filters.push({ field: 'website', operator: 'contains', value: website.trim() });
    }
    if (address) {
      filters.push({ field: 'locFormattedAddress', operator: 'contains', value: address.trim() });
    }

    return filters;
  }, [businessName, branchName, website, address]);

  const doSearch = useCallback(() => {
    performSearch(buildFilters());
  }, [performSearch, buildFilters]);

  const clearSearch = useCallback(() => {
    setBusinessName('');
    setBranchName('');
    setWebsite('');
    setAddress('');
    clearResults();
  }, [clearResults]);

  const handleView = useCallback((item: any) => {
    onView?.(item);
  }, [onView]);

  const handleAdd = useCallback((item: any) => {
    onAdd?.(item);
  }, [onAdd]);

  const handleNew = useCallback(() => {
    onNew?.();
  }, [onNew]);

  const handleRowClick = useCallback((item: any) => {
    if (searchMode === 'default') {
      handleView(item);
    } else if (showAddButton) {
      handleAdd(item);
    }
  }, [searchMode, showAddButton, handleView, handleAdd]);

  const onPageChange = useCallback((page: number) => {
    handlePageChange(page, buildFilters());
  }, [handlePageChange, buildFilters]);

  const onPageSizeChange = useCallback((size: number) => {
    handlePageSizeChange(size, buildFilters());
  }, [handlePageSizeChange, buildFilters]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      doSearch();
    }
  }, [doSearch]);

  const renderCardTitle = useCallback((item: any) => item.businessName || 'Unknown Business', []);

  return (
    <div className={`npsd-search-business ${theme}`}>
      <NPSDSearchFilters
        activeFilterCount={activeFilterCount}
        loading={isLoading}
        onSearch={doSearch}
      >
        <div className="search-fields">
          <div className="field-row">
            <div className="field">
              <label>Business Name</label>
              <input
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Business Name"
              />
            </div>
            <div className="field">
              <label>Branch Name</label>
              <input
                type="text"
                value={branchName}
                onChange={(e) => setBranchName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Branch Name"
              />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Website</label>
              <input
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Website"
              />
            </div>
            <div className="field">
              <label>Address</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Address"
              />
            </div>
          </div>
        </div>
        <div className="search-actions">
          <button type="button" className="btn btn-primary" onClick={doSearch} disabled={isLoading}>
            <i className="fa fa-search"></i> Search
          </button>
          <button type="button" className="btn btn-secondary" onClick={clearSearch}>
            <i className="fa fa-trash"></i> Clear
          </button>
          {showNewButton && (
            <button type="button" className="btn btn-success" onClick={handleNew}>
              <i className="fa fa-plus"></i> New
            </button>
          )}
        </div>
      </NPSDSearchFilters>

      {/* Results */}
      <NPSDSearchResults
        columns={columns}
        results={results}
        loading={isLoading}
        hasSearched={hasSearched}
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        renderCardTitle={renderCardTitle}
        onRowClick={handleRowClick}
        onLinkClick={handleView}
        onCurrentPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}

(window as any).NPSDSearchBusinessReact = NPSDSearchBusiness;
